import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Textarea } from "@heroui/react";
import { Archive, WarningCircle } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useState } from "react";
import { CandidateInfo } from "./types";

interface ArchiveCandidateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    candidate: CandidateInfo | null;
    isLoading?: boolean;
    isInvited?: boolean; // true for invited entities, false for applications
}

export const ArchiveCandidateModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    candidate,
    isLoading = false,
    isInvited = false
}: ArchiveCandidateModalProps) => {
    const [reason, setReason] = useState("");

    if (!candidate) return null;

    const handleConfirm = () => {
        if (!isInvited && !reason.trim()) {
            return; // Don't allow empty reason for applications
        }
        onConfirm(reason);
        setReason(""); // Reset reason after confirmation
    };

    const handleClose = () => {
        setReason(""); // Reset reason when closing
        onClose();
    };

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={handleClose}
            size="lg"
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <WarningCircle size={24} className="text-status-bad" />
                        <span className="text-status-bad font-medium">
                            {isInvited ? "Kandidaat verwijderen" : "Kandidaat archiveren"}
                        </span>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <div className="space-y-4">
                        {/* Candidate Info */}
                        <div className="bg-brick-secondary-fine p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-neutral-primary">
                                        {candidate.personalDetails.firstName} {candidate.personalDetails.lastName}
                                    </p>
                                    <p className="text-sm text-neutral-secondary-mid">
                                        {candidate.personalDetails.email}
                                    </p>
                                    <p className="text-sm text-neutral-secondary-mid">
                                        Unit: {candidate.unitId}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Confirmation message */}
                        <div className="text-neutral-primary">
                            {isInvited ? (
                                <p>
                                    Weet je zeker dat je deze kandidaat wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden.
                                </p>
                            ) : (
                                <p>
                                    Weet je zeker dat je deze kandidaat wilt archiveren? De kandidaat wordt verborgen maar blijft wel bestaan in de database.
                                </p>
                            )}
                        </div>

                        {/* Reason textarea - only for applications */}
                        {!isInvited && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-neutral-primary">
                                    Reden voor archivering <span className="text-status-bad">*</span>
                                </label>
                                <Textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Geef een reden voor het archiveren van deze kandidaat..."
                                    rows={3}
                                    classNames={{
                                        base: "w-full",
                                        input: "resize-none"
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter className="justify-start">
                    <ButtonNew
                        variant="primaryStatusRed"
                        onClick={handleConfirm}
                        icon={<Archive />}
                        label={isInvited ? "Verwijderen" : "Archiveren"}
                        isDisabled={isLoading || !isInvited && !reason.trim()}
                    />
                    <ButtonNew
                        variant="secondaryNeutral"
                        onClick={handleClose}
                        label="Annuleren"
                        isDisabled={isLoading}
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
